import {createSelector} from 'reselect';
import {includes, isEmpty, sortBy} from 'lodash';

import {State} from '../reducer';
import {licenseKey} from './types';
import {ILicense} from '../../types/License';
import {DiplomatFeature, Feature} from '../../types/DiplomatFeature';
import {ScheduleTrigger} from '../../types/Schedule';
import {TransportType} from 'types/TransportType';
import {enums} from 'types/enums';
import {daysTo} from 'common/dateUtils';

const isLicenseLoaded = (state: State) => !isEmpty(state[licenseKey].license);
const getLicense = createSelector(
   (state: State): ILicense => state[licenseKey].license,
   (license: ILicense) => ({...license, numKeys: license.numKeys + 1})
); // connected with LICENSE_UNLIMITED_NUM_KEYS

const isProfessional = createSelector(
   getLicense,
   (license) => license.edition === enums.LicenseEdition.enum.PROFESSIONAL
);
const isStandart = createSelector(
   getLicense,
   (license) => license.edition === enums.LicenseEdition.enum.STANDARD
);

const expires = (state: State) => state[licenseKey].license.expires;

export const LICENSE_UNLIMITED_NUM_KEYS = -99 + 1;
export const LICENSE_UNLIMITED = -99;
const getNumberKeysAllowed = (state: State) => state[licenseKey].license.numKeys;

export const isUnlimited = (n: number) => n === LICENSE_UNLIMITED || n === 99;

const isFeatureSupported = createSelector(getLicense, (license: ILicense) => (feature: Feature) =>
   license.edition === enums.LicenseEdition.enum.ENTERPRISE ||
   includes(license.features, feature.id)
);

const areAnyApisAllowed = createSelector(getLicense, (license: ILicense) => !!license.numApis);

export const TRIGGER_OPTION_NAME = 'Trigger Name';
export const TRIGGER_OPTION_NONE = 'None';
export const TRIGGER_OPTION_FILELOCKING = 'File Locking';

const fileReadyConditions = createSelector(isFeatureSupported, (isSupported) => {
   const conds = [TRIGGER_OPTION_NONE, TRIGGER_OPTION_FILELOCKING];
   if (isSupported(DiplomatFeature.FILE_TRIGGER)) {
      conds.unshift(TRIGGER_OPTION_NAME);
   }

   return conds;
});

const getScheduleTriggers = createSelector(isFeatureSupported, (isSupported) => {
   const conds = [ScheduleTrigger.NONE, ScheduleTrigger.SCHEDULE];
   if (isSupported(DiplomatFeature.FILESYSTEM_MONITOR_SCHEDULING)) {
      conds.push(ScheduleTrigger.FILE_MONITOR);
   }

   return conds;
});

// const transports = (state: State) => state[licenseKey]?.options?.transports;
const transports = createSelector(
   (state: State) => state[licenseKey]?.options?.transports,
   (transports: {id: string}[]) => {
      return TransportType.options.filter((type) => transports?.find((t) => t.id === type.id));
   }
);
const transportOptions = createSelector(
   (state: State) => state[licenseKey]?.options?.transports,
   (transports) =>
      sortBy(
         transports?.map((t) => ({id: t.id, name: t.display, destinationOnly: t.destinationOnly})),
         (o) => o.name.toLowerCase()
      )
);

const verifyTransfersMap = (state: State) => state[licenseKey]?.options?.verifyTransfersMap || {};

const areAllowsTransactions = (state: State) => state[licenseKey]?.license.numTransactions !== 0;

const isTrial = createSelector(getLicense, (license: ILicense) => {
   return license.type === enums.licenseType.enum.TRIAL && daysTo(license.expires) >= 0;
});

export default {
   isLicenseLoaded,
   getLicense,
   isProfessional,
   isStandart,
   expires,
   isFeatureSupported,
   areAnyApisAllowed,
   fileReadyConditions,
   transports,
   transportOptions,
   verifyTransfersMap,
   getScheduleTriggers,
   getNumberKeysAllowed,
   areAllowsTransactions,
   isTrial,
};
